import { Box, Stack, SxProps, Theme, Typography } from "@mui/material";

import InlineCode from "@/common/components/Code/InlineCode";
import { AppPermission, PermissionMetaDto } from "@/core/api/generated/v1";

import AppIcon from "../Icons/AppIcon";
import AppTypography from "../Text/AppTypography";
import AppTooltip from "../Tooltip/AppTooltip";

export interface PermissionDisplayProps {
  permission?: PermissionMetaDto | AppPermission | string;
  variant?: "inline" | "block";
  withIcon?: boolean;
  withDescription?: boolean;
  sx?: SxProps<Theme>;
}

export default function PermissionDisplay({
  permission,
  variant = "inline",
  withIcon = false,
  withDescription = false,
  sx,
}: PermissionDisplayProps) {
  const permissionDto = permission as PermissionMetaDto;
  const appPermission = permission as AppPermission | string;

  const title = permissionDto?.name || appPermission || "";
  const description = permissionDto?.description || "";

  if (variant === "inline") {
    return (
      <Typography component='span' sx={sx}>
        {withIcon && (
          <span>
            <AppIcon of='permission' color='text' />{" "}
          </span>
        )}
        <InlineCode>{title}</InlineCode>
      </Typography>
    );
  } else if (variant === "block") {
    return (
      <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1, ...sx }}>
        {withIcon && (
          <Box>
            <Typography component='div'>
              <AppIcon of='permission' color='text' />
            </Typography>
          </Box>
        )}
        <Stack>
          <AppTooltip
            enterDelay={300}
            enterNextDelay={300}
            title={
              <Box>
                <AppTypography variant='subtitle2'>Permission details</AppTypography>
                <Box>Name: {permissionDto?.name || "-"}</Box>
                <Box>Description: {permissionDto?.description || "-"}</Box>
                <Box>Value: {permissionDto?.value || appPermission || "-"}</Box>
              </Box>
            }
          >
            <AppTypography component='span' decoration={{ variant: "helpText" }}>
              {title}
            </AppTypography>
          </AppTooltip>

          {withDescription && description && (
            <Box>
              <Typography component='div' variant='body2' color='text.secondary'>
                {description}
              </Typography>
            </Box>
          )}
        </Stack>
      </Stack>
    );
  } else {
    return null;
  }
}
